import { useState } from "react";
import { ChevronLeft, ChevronRight, Check, Circle } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * StepDialog — TurboTax-style progressive disclosure inside a Dialog.
 * Breaks dense forms into bite-sized, one-topic-at-a-time screens.
 *
 * Props:
 *   open           - Dialog open state
 *   onOpenChange    - Dialog open change handler
 *   title          - Overall wizard title
 *   steps          - [{id, title, subtitle?, icon?, isComplete?, isDisabled?}]
 *   currentStep    - Active step index (0-based), controlled externally
 *   onStepChange   - (stepIndex) => void
 *   onSubmit       - Final submit callback
 *   onCancel       - Cancel callback
 *   submitLabel    - Custom submit button label
 *   submitDisabled - Disable submit button
 *   saving         - Show spinner on submit
 *   canAdvance     - Whether the current step is valid to advance (default true)
 *   children       - The active step's form content
 *   maxWidth       - Dialog max width (default "max-w-2xl")
 */
export default function StepDialog({
  open,
  onOpenChange,
  title = "New Entry",
  steps = [],
  currentStep = 0,
  onStepChange,
  onSubmit,
  onCancel,
  submitLabel = "Submit",
  submitDisabled = false,
  saving = false,
  canAdvance = true,
  children,
  maxWidth = "max-w-2xl",
  dismissible = true,
}) {
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const completedCount = steps.filter(s => s.isComplete).length;
  const pct = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${maxWidth} p-0 gap-0 max-h-[90vh] overflow-hidden`} data-testid="step-dialog">
        {/* ── Header with progress ── */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-100" data-testid="step-dialog-header">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900" data-testid="step-dialog-title">{title}</h2>
            <span className="text-xs font-semibold text-slate-400">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-600 to-teal-500 transition-all duration-500 ease-out"
              style={{ width: `${Math.max(((currentStep + 1) / steps.length) * 100, 8)}%` }}
              data-testid="step-dialog-progress"
            />
          </div>
          {/* Step pills */}
          <div className="flex items-center gap-1 mt-3 overflow-x-auto pb-1">
            {steps.map((step, idx) => {
              const isActive = idx === currentStep;
              const isDone = step.isComplete;
              const StepIcon = step.icon;
              return (
                <button
                  key={step.id || idx}
                  onClick={() => !step.isDisabled && onStepChange?.(idx)}
                  disabled={step.isDisabled}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-sky-700 text-white shadow-sm"
                      : isDone
                      ? "bg-teal-50 text-teal-700 hover:bg-teal-100"
                      : step.isDisabled
                      ? "bg-slate-50 text-slate-300 cursor-not-allowed"
                      : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                  data-testid={`step-pill-${idx}`}
                >
                  {isDone ? (
                    <Check className="w-3 h-3" />
                  ) : StepIcon ? (
                    <StepIcon className="w-3 h-3" />
                  ) : (
                    <Circle className="w-3 h-3" />
                  )}
                  {step.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Step Content ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-[280px] max-h-[55vh]" data-testid="step-dialog-content">
          {/* Step subtitle/question */}
          {steps[currentStep]?.subtitle && (
            <p className="text-sm text-slate-500 mb-5 leading-relaxed" data-testid="step-subtitle">
              {steps[currentStep].subtitle}
            </p>
          )}
          {children}
        </div>

        {/* ── Bottom Action Bar ── */}
        <div className="flex items-center justify-between px-6 py-3.5 border-t border-slate-100 bg-slate-50/50" data-testid="step-dialog-actions">
          <div>
            {!isFirst ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStepChange?.(currentStep - 1)}
                className="text-slate-500 hover:text-slate-700"
                data-testid="step-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            ) : dismissible ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel || (() => onOpenChange?.(false))}
                className="text-slate-400"
                data-testid="step-cancel-btn"
              >
                Cancel
              </Button>
            ) : <span />}
          </div>
          <div className="flex items-center gap-2">
            {!isLast ? (
              <Button
                onClick={() => onStepChange?.(currentStep + 1)}
                disabled={!canAdvance}
                className="bg-sky-700 hover:bg-sky-800 text-white"
                size="sm"
                data-testid="step-next-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={onSubmit}
                disabled={submitDisabled || saving}
                className="bg-sky-700 hover:bg-sky-800 text-white"
                size="sm"
                data-testid="step-submit-btn"
              >
                {saving && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                )}
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
